import { Phase, Step, Block, Flow } from '../types/Document';

// --- Defaults & Helpers ---
export const DEFAULT_PHASE: Phase = { name: 'New Phase', description: '', steps: [] };
export const DEFAULT_STEP: Step = {
  name: 'New Step',
  blocks: {
    instruction: {
      text: 'New instruction content'
    }
  }
};
export const DEFAULT_BLOCK: Block = { text: 'New Block Content' };

export const generateBlockKey = (): string =>
  `block_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

// Helper to format document type for display
export const formatDisplayDocumentType = (docType: string | undefined): string => {
  if (!docType) return 'Unknown Type'; // Default for undefined
  return docType.charAt(0).toUpperCase() + docType.slice(1).toLowerCase(); // Capitalize first, rest lowercase
};

// Helper to determine layout type
export const getLayoutType = (docType: string | undefined) => {
  if (docType === 'dsi') return 'stacked';
  // Default to side-by-side for 'procedure' or any other/undefined type
  return 'side-by-side';
};

// Helper to parse path string (e.g., "phase-0-1") into meaningful parts
// Returns an object: { type: 'phase', indices: [0, 1], blockKey: null } 
// or { type: 'block', indices: [0, 1, 0], blockKey: 'instruction' }
export interface ParsedPath {
  type: 'flow' | 'phase' | 'step' | 'block';
  indices: number[];
  blockKey: string | null;
}
export const parsePath = (pathString: string): ParsedPath | null => {
  const parts = pathString.split('-');
  if (parts.length < 2) return null;

  const type = parts[0] as ParsedPath['type'];
  const indices: number[] = [];
  let blockKey: string | null = null;

  for (let i = 1; i < parts.length; i++) {
    if (type === 'block' && i === parts.length - 1) {
      blockKey = "instruction"; // For block type, always use "instruction" as the key
      // This ensures we're working with the expected structure regardless of what's in the path
    } else {
      const num = parseInt(parts[i], 10);
      if (isNaN(num)) return null; // Indices must be numbers
      indices.push(num);
    }
  }

  // Basic validation
  if ((type === 'flow' && indices.length !== 1) ||
      (type === 'phase' && indices.length !== 2) ||
      (type === 'step' && indices.length !== 3) ||
      (type === 'block' && (indices.length !== 3 || !blockKey))) {
    console.error("Invalid path string format:", pathString);
    return null;
  }

  return { type, indices, blockKey };
};

// Helper to navigate state based on parsed path
export const navigateToPath = (state: Flow[], parsedPath: ParsedPath | null): any => {
  if (!parsedPath || !state) return undefined;

  try {
    let current: any = state[parsedPath.indices[0]]; // Access flow
    if (!current) return undefined;

    // Return the entire flow object (containing Flow property and phases array)
    if (parsedPath.type === 'flow') return current;

    current = current.phases[parsedPath.indices[1]]; // Access phase
    if (!current) return undefined;
    if (parsedPath.type === 'phase') return current;

    current = current.steps[parsedPath.indices[2]]; // Access step
    if (!current) return undefined;
    if (parsedPath.type === 'step') return current;

    if (parsedPath.type === 'block' && parsedPath.blockKey) {
      return current.blocks?.[parsedPath.blockKey]; // Access block
    }
  } catch (e) {
      console.error("Navigation error for path:", parsedPath, e);
      return undefined;
  }
  return undefined;
};

// Helper to navigate to parent collection based on parsed path
// Returns { collection: array | object, indexOrKey: number | string }
export const navigateToParentCollectionInfo = (
    state: Flow[],
    parsedPath: ParsedPath | null
): { collection: any[] | Record<string, Block> | null, indexOrKey: number | string | null } => {
    if (!parsedPath || !state) return { collection: null, indexOrKey: null };

    try {
        const flowIndex = parsedPath.indices[0];
        let parentCollectionOwner: any = state[flowIndex];

        if (parsedPath.type === 'phase') {
            return { collection: parentCollectionOwner?.phases, indexOrKey: parsedPath.indices[1] };
        }

        parentCollectionOwner = parentCollectionOwner?.phases?.[parsedPath.indices[1]];
        if (parsedPath.type === 'step') {
             return { collection: parentCollectionOwner?.steps, indexOrKey: parsedPath.indices[2] };
        }

        parentCollectionOwner = parentCollectionOwner?.steps?.[parsedPath.indices[2]];
        if (parsedPath.type === 'block' && parsedPath.blockKey) {
             return { collection: parentCollectionOwner?.blocks, indexOrKey: parsedPath.blockKey };
        }

    } catch (e) {
         console.error("Parent Navigation error for path:", parsedPath, e);
    }

    return { collection: null, indexOrKey: null };
};

// Function to render HTML content safely (assuming it's already sanitized or trusted)
export const renderHtml = (html: string) => {
  return { __html: html };
};

// Function to render HTML content without inline styles (styles moved to CSS file)
// Also converts \n line breaks to <br> tags for proper HTML rendering
// Collapses consecutive line breaks to prevent excessive whitespace
export const renderHtmlContent = (content: string) => {
  if (!content) return content;
  
  // First, clean up excessive whitespace and line breaks in the content
  let cleanedContent = content
    // Collapse multiple consecutive <br> tags (with optional whitespace/newlines between)
    .replace(/(<br\s*\/?>[\s\n]*){2,}/gi, '<br><br>')
    // Collapse multiple consecutive newlines into max 2
    .replace(/\n{3,}/g, '\n\n')
    // Remove excessive whitespace before tables
    .replace(/(<br\s*\/?>[\s\n]*)+(<table)/gi, '$2')
    // Remove excessive whitespace after closing tags before tables  
    .replace(/([\s\n]*<br\s*\/?>[\s\n]*)+(<table)/gi, '\n$2');
  
  // If content already has block-level HTML, don't add extra <br> tags
  if (cleanedContent.includes('<p>') || cleanedContent.includes('<div>') || cleanedContent.includes('<br')) {
    return cleanedContent;
  }
  // Convert \n to <br> for plain text or simple HTML
  return cleanedContent.replace(/\n/g, '<br>');
};

export const hasHtmlContent = (text: string) => {
  return text.includes('<table>') ||
         text.includes('<img') ||
         text.includes('<ul>') ||
         text.includes('<ol>') ||
         text.includes('<p>') ||
         text.includes('<br');
}; 